import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

/**
 * FirstAccessPage - Página de primeiro acesso
 * 
 * Migrada para Tailwind CSS com fidelidade visual ao original.
 * Exibe informações sobre LGPD e canais de atendimento.
 */
export function FirstAccessPage() {
  const { t } = useTranslation();
  // Adiciona classe page-login ao body para aplicar estilos corretos
  useEffect(() => {
    document.body.className = 'page-login';
    return () => {
      document.body.className = 'fix-header fix-sidebar page-water home';
    };
  }, []);

  // Classes para os itens da lista de canais
  const channelItemClasses = `
    flex items-center
    py-[12px] px-[15px] mb-[10px]
    bg-[rgba(255,255,255,0.05)]
    rounded-[6px]
  `;

  return (
    <div 
      id="main-wrapper" 
      className="min-h-screen bg-gradient-to-br from-navy-900 to-navy-700"
    >
      <div className="login">
        <div className="flex flex-wrap mx-0">
          {/* Login content */}
          <div className="login-content mx-auto mt-[20px] min-[813px]:mt-[50px] max-w-[480px] w-full">
            {/* Login form card — sem a classe `login-form`: o style.css legado zera
                a lateral com `padding: 0 !important` abaixo de 812px */}
            <div className="
              px-[20px] pt-[24px] pb-[20px] rounded-[2px]
              min-[813px]:px-[30px] min-[813px]:pt-[30px]
            ">

              {/* Logo */}
              <div className="login-logo text-center mb-[32px] min-[813px]:mb-[80px]">
                <img
                  src="/images/logo_login.png"
                  alt="Laager"
                  width={167}
                  height={153}
                  className="mx-auto w-[150px] min-[813px]:w-[167px]"
                />
              </div>

              {/* First access content */}
              <div className="first-access-content max-w-[360px] mx-auto">
                
                {/* Title */}
                <h4 className="text-sky-500 mb-[20px] text-[18px] font-semibold">
                  {t('views.first_access.title', 'Primeiro Acesso')}
                </h4>
                
                {/* Info box LGPD */}
                <div className="
                  bg-sky-500/15
                  rounded-[8px] p-[15px] mb-[25px]
                  border-0 border-l-[5px] border-solid border-sky-200
                  shadow-[inset_4px_0_8px_-4px_rgba(164,225,255,0.5)]
                ">
                  <p className="m-0 text-white text-[14px] leading-[1.6] font-semibold">
                    <strong className="font-bold">{t('views.first_access.lgpd_label', 'LGPD:')}</strong> {t('views.first_access.lgpd_text', 'Por questões de segurança e proteção de dados, precisamos confirmar sua identidade antes de liberar o acesso. Serão solicitados documentos que comprovem sua identidade e vínculo com a unidade.')}
                  </p>
                </div>

                {/* CTA de auto-cadastro (caminho principal) */}
                <div className="text-center mb-[25px]">
                  <Link
                    to="/registrar"
                    className="
                      inline-block w-full
                      py-[12px] px-[20px]
                      bg-lime-500 hover:bg-lime-400
                      !text-navy-900 text-[14px] font-bold
                      border-0 rounded-[24px]
                      transition-colors
                    "
                  >
                    <i className="fa fa-user-plus mr-2"></i> {t('views.first_access.create_now', 'Criar minha conta agora')}
                  </Link>
                  <p className="mt-[10px] text-[rgba(255,255,255,0.6)] text-[12px]">
                    {t('views.first_access.create_now_hint', 'Faça seu cadastro em poucos passos, direto por aqui.')}
                  </p>
                </div>

                {/* Como solicitar (canais alternativos) */}
                <h4 className="text-sky-500 mb-[20px] text-[18px] font-semibold">
                  {t('views.first_access.prefer_support', 'Prefere atendimento? Fale conosco')}
                </h4>
                
                <ul className="list-none p-0 m-0 mb-[25px]">
                  <li className={channelItemClasses}>
                    <i className="fa fa-building text-[18px] w-[30px] text-sky-500"></i>
                    <div className="flex-1">
                      <strong className="!text-white block text-[13px] !font-bold">
                        {t('views.first_access.condo_title', 'Medidor com dispositivo Laager no seu condomínio?')}
                      </strong>
                      <span className="text-[rgba(255,255,255,0.7)] text-[12px]">
                        {t('views.first_access.condo_text', 'Entre em contato com sua administradora ou com nosso suporte.')}
                      </span>
                    </div>
                  </li>
                  <li className={channelItemClasses}>
                    <i className="fa fa-map-marker text-[18px] w-[30px] text-sky-500"></i>
                    <div className="flex-1">
                      <strong className="!text-white block text-[13px] !font-bold">
                        {t('views.first_access.city_title', 'Medidor com dispositivo Laager na sua cidade?')}
                      </strong>
                      <span className="text-[rgba(255,255,255,0.7)] text-[12px]">
                        {t('views.first_access.city_text', 'Já tem um produto Laager instalado na sua residência? Procure a agência de atendimento do serviço da concessionária da sua cidade e solicite o cadastro.')}
                      </span>
                    </div>
                  </li>
                </ul>

                {/* Atendimento */}
                <h4 className="text-sky-500 mb-[20px] text-[18px] font-semibold">
                  {t('views.first_access.support_title', 'Atendimento Laager')}
                </h4>
                
                <ul className="list-none p-0 m-0 mb-[25px]">
                  {/* WhatsApp */}
                  <li className={channelItemClasses}>
                    <i className="fa fa-whatsapp text-[18px] w-[30px] text-[#25D366]"></i>
                    <div className="flex-1">
                      <strong className="!text-white block text-[13px] !font-bold">WhatsApp</strong>
                      <a 
                        href="https://wa.me/5511940004446"
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-[rgba(255,255,255,0.7)] text-[12px] hover:text-sky-200 hover:no-underline"
                      >
                        (11) 94000-4446
                      </a>
                    </div>
                  </li>
                  
                  {/* E-mail */}
                  <li className={channelItemClasses}>
                    <i className="fa fa-envelope text-[18px] w-[30px] text-sky-500"></i>
                    <div className="flex-1">
                      <strong className="!text-white block text-[13px] !font-bold">{t('views.login.email_placeholder', 'E-mail')}</strong>
                      <a 
                        href="mailto:atendimento@laager.com.br"
                        className="text-[rgba(255,255,255,0.7)] text-[12px] hover:text-sky-200 hover:no-underline"
                      >
                        atendimento@laager.com.br
                      </a>
                    </div>
                  </li>
                  
                  {/* Telefone */}
                  <li className={channelItemClasses}>
                    <i className="fa fa-phone text-[18px] w-[30px] text-sky-500"></i>
                    <div className="flex-1">
                      <strong className="!text-white block text-[13px] !font-bold">{t('views.first_access.phone_label', 'Telefone')}</strong>
                      <a 
                        href="tel:+551130342350"
                        className="text-[rgba(255,255,255,0.7)] text-[12px] hover:text-sky-200 hover:no-underline"
                      >
                        (11) 3034-2350
                      </a>
                    </div>
                  </li>
                </ul>

                {/* Back button */}
                <div className="text-center">
                  <Link 
                    to="/login" 
                    className="
                      inline-block mt-[10px]
                      py-[8px] px-[20px]
                      bg-white/10 hover:bg-white/20
                      !text-white text-[12px] font-medium
                      border-0 rounded-[20px]
                      transition-colors
                    "
                  >
                    <i className="fa fa-arrow-left mr-2"></i> {t('common.back_to_login', 'Voltar para o login')}
                  </Link>
                </div>
              </div>

              {/* Footer logo */}
              <div className="text-center mt-[50px] mb-[90px]">
                <img src="/images/logo-footer.png" alt="Laager" />
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
